import React, { Dispatch, FC, SetStateAction, useRef } from 'react';

import getStyles from 'helpers/getStyles';
import { isVideoCard } from 'helpers/isVideoCard';

import { CardSchema, ECardLayoutOptions } from 'constants/graphqlTypes';

import { StyledImageWrap } from './styled';

interface ImageProps {
  card: CardSchema;
  setIsVertical: Dispatch<SetStateAction<boolean>>;
  dataTestId?: string;
  imageRef?: React.Ref<HTMLDivElement>;
  imageAnimationProgress?: number;
  isImageAnimating?: boolean;
  interpolatedRotation?: number;
  interpolatedScale?: number;
  onImageLoad?: () => void;
}

const Image: FC<ImageProps> = ({
  card,
  dataTestId,
  setIsVertical,
  imageRef,
  imageAnimationProgress = 0,
  isImageAnimating = false,
  interpolatedRotation = 0,
  interpolatedScale = 1,
  onImageLoad,
}) => {
  const { cardStyle, title, displayTitle, displayThumbnailUrl, sourceName } = card;
  const isVideo = isVideoCard(card);
  const isTiktokVideo = isVideo && sourceName === 'tiktok';
  const { layout } = getStyles(cardStyle);
  const imageUrl = displayThumbnailUrl ?? cardStyle?.thumbnailImageSet?.images?.[0]?.url;

  const imgRef = useRef<HTMLImageElement>(null);

  const showImage = imageUrl && layout !== ECardLayoutOptions.Plain;
  const isBorderless = layout === ECardLayoutOptions.Borderless;

  const handleLoad = () => {
    if (imgRef?.current) {
      setIsVertical(imgRef?.current.width < imgRef?.current?.height);
    }
    onImageLoad?.();
  };

  return showImage ? (
    <StyledImageWrap
      ref={imageRef}
      isBorderless={isBorderless}
      isTiktokVideo={isTiktokVideo}
      imageAnimationProgress={imageAnimationProgress}
      isImageAnimating={isImageAnimating}
      interpolatedRotation={interpolatedRotation}
      interpolatedScale={interpolatedScale}
    >
      <img onLoad={handleLoad} ref={imgRef} data-testid={dataTestId} src={imageUrl} alt={displayTitle ?? title ?? ''} />
    </StyledImageWrap>
  ) : null;
};

export default Image;
